/**
 * EstimateField — the work item detail rail's estimate control (FLOW-903).
 *
 * A number input with the project's estimate unit beside it as a static
 * suffix, except under `t_shirt_size` where the number input is replaced by a
 * Select over the ladder (XS…XL) whose labels carry the unit themselves. The
 * unit is the project's, not the item's (FLOW-909), so the field states it
 * rather than offering it; it is changed in Project Settings.
 *
 * The number input is deliberately *uncontrolled*. `<input type="number">`
 * sanitises every partial entry — `7.`, `-`, `1e` — to an empty string, so a
 * value bound to the input's own events writes that empty string straight back
 * and wipes the digits the user can see: with `step="0.5"` the decimal point is
 * the normal path, not an edge case. Its value is therefore written only when
 * it mounts, when the props change underneath it, and on Escape; it is read
 * only when a commit is due: focus leaving the field, or Enter.
 */

import { Component, Show, createEffect, createMemo, untrack } from "solid-js";
import type { EstimateUnit } from "../../api/types";
import { Select } from "../ui/Select";
import { T_SHIRT_LADDER, UNIT_SUFFIX, tShirtLabel } from "../../utils/estimate";

export interface EstimateFieldProps {
  value: number | null;
  unit: EstimateUnit;
  onSave: (p: { estimate_value: number; estimate_unit: EstimateUnit }) => void;
  onClear: () => void;
}

/** The no-estimate option on the size Select, and the number input's placeholder. */
const EMPTY_LABEL = "—";

/** What the number input holds: a usable number, nothing, or something unusable. */
type Entry = { value: number } | "empty" | "invalid";

export const EstimateField: Component<EstimateFieldProps> = (props) => {
  const asText = (value: number | null) => (value == null ? "" : String(value));

  let input: HTMLInputElement | undefined;

  const unit = () => props.unit;

  /** `null` under t_shirt_size: the ladder's own labels are the unit. */
  const suffix = () => {
    const u = unit();
    return u === "t_shirt_size" ? null : UNIT_SUFFIX[u];
  };

  // Write the input only when the prop actually changes: an in-progress entry
  // survives unrelated re-renders, and a committed value is never briefly
  // rolled back to the stale one while its PATCH is in flight.
  let seen = props.value;
  createEffect(() => {
    const value = props.value;
    if (value !== seen && input) input.value = asText(value);
    seen = value;
  });

  const sizeOptions = createMemo(() => {
    const rungs = [...T_SHIRT_LADDER];
    const current = props.value;
    if (current != null && !rungs.includes(current)) rungs.push(current);
    return [
      { value: "", label: EMPTY_LABEL },
      ...rungs
        .sort((a, b) => a - b)
        .map((rung) => ({ value: String(rung), label: tShirtLabel(rung) })),
    ];
  });

  const revert = () => {
    if (input) input.value = asText(props.value);
  };

  /**
   * What the number input currently holds, when it is the live control at all:
   * <Show> leaves `input` pointing at a detached node under `t_shirt_size`, and
   * a stale value there must never reach a save.
   *
   * `badInput` is the browser's own verdict on a partial entry: the value reads
   * back empty, and mistaking that for a deliberately emptied field would
   * delete the stored estimate when focus leaves.
   */
  const entered = (): Entry => {
    if (unit() === "t_shirt_size") return "invalid";
    if (!input || input.validity.badInput) return "invalid";
    const raw = input.value.trim();
    if (raw === "") return "empty";
    const parsed = Number(raw);
    if (!Number.isFinite(parsed) || parsed < 0) return "invalid";
    return { value: parsed };
  };

  const commit = () => {
    const entry = entered();
    if (entry === "invalid") {
      revert();
      return;
    }
    if (entry === "empty") {
      if (props.value != null) props.onClear();
      else revert();
      return;
    }
    if (entry.value === props.value) {
      revert();
      return;
    }
    props.onSave({ estimate_value: entry.value, estimate_unit: unit() });
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commit();
    } else if (e.key === "Escape") {
      e.preventDefault();
      revert();
    }
  };

  const handleSize = (value: string) => {
    if (value === "") {
      if (props.value != null) props.onClear();
      return;
    }
    if (Number(value) === props.value) return;
    props.onSave({
      estimate_value: Number(value),
      estimate_unit: "t_shirt_size",
    });
  };

  return (
    // `focusout` bubbles, so binding it on the wrapper catches focus leaving
    // whichever control is live — the number input, or the ladder Select that
    // replaces it under `t_shirt_size`, where `commit` is a no-op because
    // `entered()` refuses to read the detached input.
    <div class="flex items-center gap-2" onFocusOut={commit}>
      <div class="flex-1 min-w-0" data-estimate-value>
        <Show
          when={unit() === "t_shirt_size"}
          fallback={
            <input
              ref={(el) => {
                input = el;
                // Untracked: this runs inside <Show>'s branch memo, and taking a
                // dependency there would re-create the input — discarding focus
                // and any half-typed entry — on every value change.
                el.value = untrack(() => asText(props.value));
              }}
              type="number"
              min="0"
              step="0.5"
              aria-label="Estimate value"
              placeholder={EMPTY_LABEL}
              class="w-full h-7 px-3 py-0 text-sm rounded border border-line bg-surface text-ink placeholder:text-ink-3 focus-ring"
              onKeyDown={handleKeyDown}
            />
          }
        >
          <Select
            options={sizeOptions()}
            value={asText(props.value)}
            onChange={handleSize}
          />
        </Show>
      </div>
      <Show when={suffix()}>
        {(text) => (
          <span class="shrink-0 text-sm text-ink-3" data-estimate-unit>
            {text()}
          </span>
        )}
      </Show>
    </div>
  );
};
